import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { teamsAPI } from '../../services/meetingsAPI';
import { toast } from 'react-toastify';
import './CreateTeam.css';

const CreateTeam = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  
  // Form states
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [members, setMembers] = useState([{ name: '', email: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          setIsAuthenticated(true);
        } else {
          setCurrentUser(null);
          setIsAuthenticated(false);
          navigate('/meeting-home');
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setCurrentUser(null);
        setIsAuthenticated(false);
        navigate('/meeting-home');
      }
    };

    checkAuth();
  }, [navigate]);

  // Fetch user's teams
  useEffect(() => {
    const fetchTeams = async () => {
      if (!isAuthenticated || !currentUser) return;

      setIsLoadingTeams(true);
      try {
        const response = await teamsAPI.getMyTeams();
        setTeams(response.data || []);
      } catch (error) {
        console.error('Error fetching teams:', error);
        toast.error('Failed to load teams');
      } finally {
        setIsLoadingTeams(false);
      }
    };

    if (isAuthenticated && currentUser) {
      fetchTeams();
    }
  }, [isAuthenticated, currentUser]);

  const addMember = () => {
    setMembers([...members, { name: '', email: '' }]);
  };

  const removeMember = (index) => {
    if (members.length > 1) {
      setMembers(members.filter((_, i) => i !== index));
    }
  };

  const updateMember = (index, field, value) => {
    const newMembers = [...members];
    newMembers[index][field] = value;
    setMembers(newMembers);
  };

  const resetForm = () => {
    setTeamName('');
    setMembers([{ name: '', email: '' }]);
    setEditingTeamId(null);
  };

  const handleEdit = (team) => {
    setTeamName(team.teamName);
    setMembers(
      team.members && team.members.length > 0
        ? team.members.map(m => ({ name: m.name, email: m.email }))
        : [{ name: '', email: '' }]
    );
    setEditingTeamId(team._id);
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await teamsAPI.deleteTeam(teamId);
      if (response.data.success) {
        toast.success('Team deleted successfully');
        setTeams(teams.filter(t => t._id !== teamId));
        if (editingTeamId === teamId) {
          resetForm();
        }
      } else {
        toast.error(response.data.message || 'Failed to delete team');
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error(error.response?.data?.message || 'Failed to delete team');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!teamName || teamName.trim() === '') {
      toast.error('Please enter a team name');
      return;
    }

    const validMembers = members.filter(m => m.name.trim() !== '' && m.email.trim() !== '');
    if (validMembers.length === 0) {
      toast.error('Please add at least one team member with name and email');
      return;
    }

    setIsSubmitting(true);
    try {
      const teamData = {
        teamName: teamName.trim(),
        members: validMembers.map(m => ({
          name: m.name.trim(),
          email: m.email.trim()
        }))
      };

      if (editingTeamId) {
        // Update existing team
        const response = await teamsAPI.updateTeam(editingTeamId, teamData);
        if (response.data.success) {
          toast.success('Team updated successfully');
          const updatedTeams = teams.map(t => 
            t._id === editingTeamId ? response.data.data : t
          );
          setTeams(updatedTeams);
          resetForm();
        } else {
          toast.error(response.data.message || 'Failed to update team');
        }
      } else {
        // Create new team
        const response = await teamsAPI.createTeam(teamData);
        if (response.data.success) {
          toast.success('Team created successfully');
          setTeams([response.data.data, ...teams]);
          resetForm();
        } else {
          toast.error(response.data.message || 'Failed to create team');
        }
      }
    } catch (error) {
      console.error('Error saving team:', error);
      toast.error(error.response?.data?.message || 'Failed to save team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-blue-50 page-content create-team-page">
      <Navbar isScrolled={false} showLoginModal={true} />
      
      <div className="w-full px-4 py-8 create-team-wrap">
        <div className="create-team-shell mx-auto">
          <div className="create-team-header-shell">
            <div className="flex items-center mb-5 create-team-back-row">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="create-team-back-button"
                aria-label="Go back"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            </div>

            <div className="create-team-page-heading">
              <h1 className="create-team-page-title">Team Management</h1>
              <p className="create-team-page-subtitle">
                Create and manage your teams for meeting room bookings.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 create-team-grid">
            {/* Create/Edit Team Form */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 create-team-panel create-team-form-panel">
              <div className="create-team-panel-head">
                <div>
                  <span className="create-team-panel-kicker">Setup</span>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingTeamId ? 'Edit Team' : 'Create New Team'}
                  </h2>
                </div>
                <div className="create-team-panel-badge">
                  {members.length} row{members.length !== 1 ? 's' : ''}
                </div>
              </div>

              <p className="create-team-panel-description">
                Add team members with names and email addresses so they are ready during meeting room booking.
              </p>

              <h2 className="sr-only">
                {editingTeamId ? 'Edit Team' : 'Create New Team'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6 create-team-form">
                {/* Team Name */}
                <div className="create-team-field-block">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 create-team-field-label">
                    Team Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 text-gray-700 transition-colors create-team-input"
                    placeholder="Enter team name"
                    required
                  />
                </div>

                {/* Team Members */}
                <div className="create-team-field-block create-team-members-section">
                  <div className="flex items-center justify-between mb-4 create-team-members-header">
                    <label className="block text-sm font-semibold text-gray-700 create-team-field-label">
                      Team Members <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={addMember}
                      className="create-team-add-member"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Member
                    </button>
                  </div>

                  <div className="space-y-3 create-team-member-list">
                    {members.map((member, index) => (
                      <div key={index} className="flex gap-3 items-start p-4 bg-gray-50 rounded-xl border-2 border-gray-200 create-team-member-row">
                        <div className="flex-1 space-y-3">
                          <input
                            type="text"
                            value={member.name}
                            onChange={(e) => updateMember(index, 'name', e.target.value)}
                            className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700 create-team-input"
                            placeholder="Member Name"
                            required
                          />
                          <input
                            type="email"
                            value={member.email}
                            onChange={(e) => updateMember(index, 'email', e.target.value)}
                            className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-gray-700 create-team-input"
                            placeholder="member@example.com"
                            required
                          />
                        </div>
                        {members.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeMember(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-1 create-team-remove-member"
                            title="Remove Member"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-4 pt-4">
                  {editingTeamId && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-bold text-lg rounded-xl hover:bg-gray-300 transition-colors create-team-secondary-btn"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed create-team-primary-btn"
                  >
                    {isSubmitting ? 'Saving...' : editingTeamId ? 'Update Team' : 'Create Team'}
                  </button>
                </div>
              </form>
            </div>

            {/* Teams List */}
            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 create-team-panel create-team-list-panel">
              <div className="create-team-panel-head">
                <div>
                  <span className="create-team-panel-kicker">Directory</span>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Your Teams ({teams.length})
                  </h2>
                </div>
                <div className="create-team-panel-badge create-team-panel-badge-soft">
                  Live list
                </div>
              </div>

              <p className="create-team-panel-description">
                Review existing teams, quickly edit members, or remove teams you no longer use.
              </p>

              {isLoadingTeams ? (
                <div className="text-center py-12 create-team-empty-state">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-6 animate-spin">
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
                  </div>
                  <p className="text-gray-600 font-medium">Loading teams...</p>
                </div>
              ) : teams.length === 0 ? (
                <div className="text-center py-12 create-team-empty-state">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg
                      className="w-12 h-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg font-medium mb-2">No teams created yet</p>
                  <p className="text-gray-400 text-sm">Create your first team using the form</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto create-team-scroll">
                  {teams.map((team) => (
                    <div
                      key={team._id}
                      className="border-2 border-gray-200 rounded-xl p-5 bg-gradient-to-br from-gray-50 to-white hover:shadow-lg transition-all create-team-list-card"
                    >
                      <div className="flex justify-between items-start mb-4 create-team-card-header">
                        <div className="flex-1 create-team-card-copy">
                          <div className="create-team-card-title-row">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                              {team.teamName}
                            </h3>
                            <span className="create-team-card-count">
                              {team.members?.length || 0} member{team.members?.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mb-3 create-team-card-caption">
                            Ready for meeting room booking and team selection.
                          </p>
                        </div>
                        <div className="flex gap-2 create-team-card-actions">
                          <button
                            onClick={() => handleEdit(team)}
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors create-team-icon-button create-team-icon-button-edit"
                            title="Edit Team"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(team._id)}
                            disabled={loading}
                            className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors disabled:opacity-50 create-team-icon-button create-team-icon-button-delete"
                            title="Delete Team"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Members List */}
                      {team.members && team.members.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200 create-team-members-block">
                          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                            Members
                          </p>
                          <div className="space-y-2">
                            {team.members.map((member, index) => (
                              <div key={index} className="flex items-center gap-3 text-sm create-team-member-pill">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-semibold create-team-member-avatar">
                                  {member.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{member.name}</p>
                                  <p className="text-gray-500 text-xs">{member.email}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTeam;
